import type { ApiClient } from '@argzombie/api-client';
import { ok } from './api';

/*
 * The Stories & Tech Step 2 admin calls. None of these endpoints is in the generated client yet (the S2 API streams are building them),
 * so each is a TYPED STUB: a local request/response type and a call through an untyped view of the same openapi-fetch client (it
 * substitutes `{id}` path params at runtime like any typed call). When a stream ships its endpoint, regenerate the client, delete the
 * local type, and switch the call to the typed path. The player-support views show "Not available yet" on a 404.
 *
 * | Function | Endpoint | Owner | State |
 * |---|---|---|---|
 * | `playerOccupancies` | `GET /admin/players/{id}/occupancies` | HUM | stub |
 * | `playerRescueAttempts` | `GET /admin/players/{id}/rescue-attempts` | CR | stub |
 * | `grantRescueClaim` | `POST /admin/rescue-attempts/{id}/grant-claim` | CR | stub |
 * | `voidRescueClaim` | `POST /admin/rescue-attempts/{id}/void-claim` | CR | stub |
 * | `playerQuests` | `GET /admin/players/{id}/quests` | KEY | stub |
 * | `replacePiece` | `POST /admin/quest-pieces/{id}/replace` | KEY | stub |
 * | `voidSite` | `POST /admin/key-part-sites/{id}/void` | KEY | stub |
 * | `whereWouldItSpawn` | `POST /admin/key-parts/where` | KEY | stub |
 * | `playerBuildOrders` | `GET /admin/players/{id}/build-orders` | TEC | stub |
 */

type Result<T> = { data?: T; error?: unknown; response: Response };
type Init = {
  params?: { path?: Record<string, string | number>; query?: Record<string, unknown> };
  body?: unknown;
};
type Loose = {
  GET: <T>(path: string, init?: Init) => Promise<Result<T>>;
  POST: <T>(path: string, init?: Init) => Promise<Result<T>>;
};
/** The same client, without the path typing (for endpoints the generated spec doesn't have yet). */
const loose = (c: ApiClient) => c as unknown as Loose;

// ----- occupied (S2-HUM): what was rolled at a destination and what happened -----------------------------------------------------

export type OccupancyAdminRow = {
  id: string;
  runId: string;
  buildingId: number;
  buildingName: string | null;
  crewId: string;
  disposition: string;
  /** Gate → the tell ids rolled (true and herring). */
  tells: Record<string, string[]>;
  /** Approach → the stance rolled. */
  stances: Record<string, string>;
  /** `open`, `raided`, `traded`, `cleared`, `expired`. */
  state: string;
  createdAt: string;
  raids: {
    raidId: string;
    approach: string;
    stance: string;
    outcome: string;
    kills: number;
    trades: number;
    recruitId: string | null;
  }[];
};

export const playerOccupancies = async (client: ApiClient, playerId: string) =>
  ok(
    await loose(client).GET<OccupancyAdminRow[]>('/admin/players/{id}/occupancies', {
      params: { path: { id: playerId } },
    }),
  );

// ----- rescues (S2-CR): a run's attempts with the hidden variables and the rejection reason; grant or void a held claim -------------

export type RescueAttemptAdminRow = {
  id: string;
  runId: string;
  raidId: string | null;
  scenarioId: string;
  /** `radio`, `short_handed`, `shared`. */
  source: string;
  /** `issued`, `rescued`, `failed`, `rejected`, `claim_pending`, `claimed`, `claim_void`, `expired`. */
  state: string;
  /** The ticket's signed vars: the trapped identities and hidden truths (`trapped.bitten` → `old`). */
  hidden: Record<string, string>;
  rejectReason: string | null;
  survivorId: string | null;
  claimExpiresAt: string | null;
  createdAt: string;
};

export const playerRescueAttempts = async (client: ApiClient, playerId: string) =>
  ok(
    await loose(client).GET<RescueAttemptAdminRow[]>('/admin/players/{id}/rescue-attempts', {
      params: { path: { id: playerId } },
    }),
  );

export const grantRescueClaim = async (client: ApiClient, attemptId: string, reason: string) =>
  ok(
    await loose(client).POST<unknown>('/admin/rescue-attempts/{id}/grant-claim', {
      params: { path: { id: attemptId } },
      body: { reason },
    }),
  );

export const voidRescueClaim = async (client: ApiClient, attemptId: string, reason: string) =>
  ok(
    await loose(client).POST<unknown>('/admin/rescue-attempts/{id}/void-claim', {
      params: { path: { id: attemptId } },
      body: { reason },
    }),
  );

// ----- key-part hunts (S2-KEY): the quest inspector, staff actions, and "where would it spawn?" -------------------------------------

export type QuestPieceAdminRow = {
  id: string;
  partId: string;
  itemId: string;
  /** `placed`, `carried`, `acquired`, `lost`, `respawning`. */
  state: string;
  siteId: string | null;
  raidId: string | null;
  teamId: string | null;
  updatedAt: string;
};

export type QuestSiteAdminRow = {
  id: string;
  partId: string;
  buildingId: number;
  buildingName: string | null;
  /** The picker step it fell to (1–6). */
  step: number;
  siteKindId: string | null;
  virtualSiteId: string | null;
  expiresAt: string;
  voided: boolean;
};

export type QuestAdminRow = {
  id: string;
  runId: string;
  tier: number;
  state: string;
  startedAt: string;
  completedAt: string | null;
  sites: QuestSiteAdminRow[];
  pieces: QuestPieceAdminRow[];
};

export const playerQuests = async (client: ApiClient, playerId: string) =>
  ok(
    await loose(client).GET<QuestAdminRow[]>('/admin/players/{id}/quests', {
      params: { path: { id: playerId } },
    }),
  );

export const replacePiece = async (client: ApiClient, pieceId: string, reason: string) =>
  ok(
    await loose(client).POST<unknown>('/admin/quest-pieces/{id}/replace', {
      params: { path: { id: pieceId } },
      body: { reason },
    }),
  );

export const voidSite = async (client: ApiClient, siteId: string, reason: string) =>
  ok(
    await loose(client).POST<unknown>('/admin/key-part-sites/{id}/void', {
      params: { path: { id: siteId } },
      body: { reason },
    }),
  );

export type WhereSpawnRequest = { lat: number; lon: number; partIds?: string[] };
export type WhereSpawnPart = {
  partId: string;
  /** The site picker step it fell to: 1 site kind in range … 6 virtual site. */
  step: number;
  stepName: string;
  siteKindId: string | null;
  buildingId: number | null;
  buildingName: string | null;
  lat: number | null;
  lon: number | null;
  distanceM: number | null;
  virtualSiteId: string | null;
};
export type WhereSpawnResponse = {
  parts: WhereSpawnPart[];
  /** Buildings looked at around the point. */
  buildingsScanned: number;
};

/** The site picker's six steps (macguffin.html §3), for the step column and the hit-rate summary. */
export const PICKER_STEPS = [
  '',
  'a matching site kind in range',
  'a matching building swapped into the set',
  'a fallback category',
  'a fallback category, further out',
  'any raidable building',
  'a virtual site',
] as const;

/** Step-1 share of the placed parts (how often a real site kind was found in range). */
export const stepOneRate = (parts: WhereSpawnPart[]) =>
  parts.length ? parts.filter((p) => p.step === 1).length / parts.length : 0;

export const whereWouldItSpawn = async (client: ApiClient, req: WhereSpawnRequest) =>
  ok(await loose(client).POST<WhereSpawnResponse>('/admin/key-parts/where', { body: req }));

// ----- the workshop (S2-TEC): build orders -------------------------------------------------------------------------------------------

export type BuildOrderAdminRow = {
  id: string;
  runId: string;
  itemId: string | null;
  moduleId: string | null;
  quantity: number;
  state: string;
  createdAt: string;
  finishesAt: string;
  steps: {
    idx: number;
    recipeId: string;
    times: number;
    station: string;
    startsAt: string;
    endsAt: string;
    doneAt: string | null;
  }[];
  reserved: { itemId: string; quantity: number }[];
  reservedWh: number;
};

export const playerBuildOrders = async (client: ApiClient, playerId: string) =>
  ok(
    await loose(client).GET<BuildOrderAdminRow[]>('/admin/players/{id}/build-orders', {
      params: { path: { id: playerId } },
    }),
  );
